import React, { useEffect, useState } from 'react';
import { createHotelWithManager, updateHotelAndManager } from '../utils/helperFunctions';
import { toast } from 'react-toastify';

const HotelFormModal = ({
    showModal,
    isEditing,
    setShowModal,
    fetchHotels,
    hotelEditFormData
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [previewImage, setPreviewImage] = useState([]);
    const [errorMsg, setErrorMsg] = useState("");
    const [documents, setDocuments] = useState([]);
    const [currentHotel, setCurrentHotel] = useState({
        hotelName: "",
        location: "",
        city: "",
        state: "",
        country: "",
        mobileNumber: "",
        pathikUsername: "",
        pathikPassword: ""
    });
    const [managerDetails, setManagerDetails] = useState({
        name: "",
        email: "",
        mobile: "",
        password: "",
        role: "manager",
    });
    const [errors, setErrors] = useState({
        hotel: {
            hotelName: '',
            location: '',
            city: '',
            state: '',
            country: '',
            mobileNumber: '',
            pathikUsername: '',
            pathikPassword: ''
        },
        manager: {
            name: '',
            email: '',
            mobile: '',
            password: ''
        },
        documents: []
    });
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape' && showModal) {
                handleCancel();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [showModal]);

    useEffect(() => {
        if (hotelEditFormData) {
            setCurrentHotel(hotelEditFormData.hotel);
            setManagerDetails(hotelEditFormData.manager);
            setDocuments(hotelEditFormData.documents || []);
        }
        setErrorMsg("");
        setPreviewImage([]);
    }, [hotelEditFormData, showModal]);

    if (!showModal) return null;

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setCurrentHotel(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({
            ...prev,
            hotel: {
                ...prev.hotel,
                [name]: ''
            }
        }));
    };

    const handleManagerInputChange = (e) => {
        const { name, value } = e.target;
        setManagerDetails(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({
            ...prev,
            manager: {
                ...prev.manager,
                [name]: ''
            }
        }));
    };

    const handleDocInputChange = (e, index) => {
        const { name, value } = e.target;
        setDocuments(prevDocs => {
            const updated = [...prevDocs];
            updated[index] = { ...updated[index], [name]: value };
            return updated;
        });
        setErrors(prev => {
            const newErrors = { ...prev };
            if (newErrors.documents[index]) {
                newErrors.documents[index] = {
                    ...newErrors.documents[index],
                    [name]: ''
                };
            }
            return newErrors;
        });
    };

    const handleFileChange = (e, index) => {
        const { files } = e.target;
        if (files.length > 0) {
            const file = files[0];
            setDocuments(prevDocs => {
                const updated = [...prevDocs];
                updated[index] = { ...updated[index], file };
                return updated;
            });
            setPreviewImage((prevImages) => {
                const updatedImages = [...prevImages];
                updatedImages[index] = URL.createObjectURL(file);
                return updatedImages;
            });
            setErrors(prev => {
                const newErrors = { ...prev };
                if (newErrors.documents[index]) {
                    newErrors.documents[index] = {
                        ...newErrors.documents[index],
                        file: ''
                    };
                }
                return newErrors;
            });
        }
    };

    const handleSaveHotel = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);

        try {
            const formData = new FormData();
            Object.keys(currentHotel).forEach(key => {
                formData.append(key, currentHotel[key]);
            });

            Object.keys(managerDetails).forEach(key => {
                if (key !== 'documents' && key !== '_id' && managerDetails[key]) {
                    formData.append(`manager[${key}]`, managerDetails[key]);
                }
            });

            if (managerDetails._id) {
                formData.append('manager[_id]', managerDetails._id);
            }

            documents.forEach((doc, index) => {
                formData.append(`manager[documents][${index}][documentType]`, doc.documentType || "");
                formData.append(`manager[documents][${index}][documentNumber]`, doc.documentNumber || "");
                if (doc.file) {
                    formData.append(`manager[documents][${index}][documentUrl]`, doc.file);
                } else if (doc.documentUrl) {
                    formData.append(`manager[documents][${index}][documentUrl]`, doc.documentUrl);
                }
            });
            let response;
            if (isEditing) {
                response = await updateHotelAndManager(formData);
            } else {
                response = await createHotelWithManager(formData);
            }

            if (response.status) {
                toast.success(response?.data?.msg)
                fetchHotels();
                setShowModal(false);
                setPreviewImage([])
            }
        } catch (error) {
            setErrorMsg(error.response?.data?.error || error.response?.data?.msg || "An error occurred");
            console.error("Error saving hotel:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        setShowModal(false);
        setPreviewImage([]);
        setErrors({
            hotel: {
                hotelName: '',
                location: '',
                city: '',
                state: '',
                country: '',
                mobileNumber: ''
            },
            manager: {
                name: '',
                email: '',
                mobile: '',
                password: ''
            },
            documents: []
        });
    }

    const validateForm = () => {
        let isValid = true;
        const newErrors = {
            hotel: {
                hotelName: '',
                location: '',
                city: '',
                state: '',
                country: '',
                mobileNumber: ''
            },
            manager: {
                name: '',
                email: '',
                mobile: '',
                password: ''
            },
            documents: []
        };

        if (!currentHotel.hotelName.trim()) {
            newErrors.hotel.hotelName = 'Hotel name is required';
            isValid = false;
        }
        if (!currentHotel.location.trim()) {
            newErrors.hotel.location = 'Location is required';
            isValid = false;
        }
        if (!currentHotel.city.trim()) {
            newErrors.hotel.city = 'City is required';
            isValid = false;
        }
        if (!currentHotel.state.trim()) {
            newErrors.hotel.state = 'State is required';
            isValid = false;
        }
        if (!currentHotel.country.trim()) {
            newErrors.hotel.country = 'Country is required';
            isValid = false;
        }
        if (currentHotel.mobileNumber && !/^\d{10}$/.test(currentHotel.mobileNumber)) {
            newErrors.hotel.mobileNumber = 'Mobile number must be 10 digits';
            isValid = false;
        }

        if (!managerDetails.name.trim()) {
            newErrors.manager.name = 'Manager name is required';
            isValid = false;
        }
        if (!managerDetails.email.trim()) {
            newErrors.manager.email = 'Email is required';
            isValid = false;
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(managerDetails.email)) {
            newErrors.manager.email = 'Invalid email format';
            isValid = false;
        }
        if (!managerDetails.mobile.trim()) {
            newErrors.manager.mobile = 'Mobile number is required';
            isValid = false;
        } else if (!/^\d{10}$/.test(managerDetails.mobile)) {
            newErrors.manager.mobile = 'Mobile number must be 10 digits';
            isValid = false;
        }
        if (!isEditing && !managerDetails.password.trim()) {
            newErrors.manager.password = 'Password is required';
            isValid = false;
        }

        const docErrors = documents.map((doc, index) => {
            const docError = {};
            if (!doc.documentType.trim()) {
                docError.documentType = 'Document type is required';
                isValid = false;
            }
            if (!doc.documentNumber.trim()) {
                docError.documentNumber = 'Document number is required';
                isValid = false;
            }
            if ((!isEditing && !doc.file) || (isEditing && !doc.file && !doc.documentUrl)) {
                docError.file = 'Document file is required';
                isValid = false;
            }
            return docError;
        });

        newErrors.documents = docErrors;
        setErrors(newErrors);
        return isValid;
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto" style={{
            overflow: 'auto',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
        }}>
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block">
                <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                    <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={handleCancel}></div>
                </div>
                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true"></span>
                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle max-w-4xl w-full">
                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                            {isEditing ? "Edit Hotel" : "Add New Hotel"}
                        </h3>
                        <form onSubmit={handleSaveHotel} className="space-y-4">
                            {/* Hotel Details Section */}
                            <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-3 sm:p-4 rounded-lg">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Hotel Name *</label>
                                        <input
                                            type="text"
                                            name="hotelName"
                                            value={currentHotel.hotelName}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                            required
                                        />
                                        {errors.hotel.hotelName && (
                                            <p className="mt-1 text-sm text-red-600">{errors.hotel.hotelName}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Location *</label>
                                        <input
                                            type="text"
                                            name="location"
                                            value={currentHotel.location}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                            required
                                        />
                                        {errors.hotel.location && (
                                            <p className="mt-1 text-sm text-red-600">{errors.hotel.location}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                                        <input
                                            type="text"
                                            name="city"
                                            value={currentHotel.city}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                            required
                                        />
                                        {errors.hotel.city && (
                                            <p className="mt-1 text-sm text-red-600">{errors.hotel.city}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">State *</label>
                                        <input
                                            type="text"
                                            name="state"
                                            value={currentHotel.state}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                            required
                                        />
                                        {errors.hotel.state && (
                                            <p className="mt-1 text-sm text-red-600">{errors.hotel.state}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Country *</label>
                                        <input
                                            type="text"
                                            name="country"
                                            value={currentHotel.country}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                            required
                                        />
                                        {errors.hotel.country && (
                                            <p className="mt-1 text-sm text-red-600">{errors.hotel.country}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number</label>
                                        <input
                                            type="text"
                                            name="mobileNumber"
                                            value={currentHotel.mobileNumber}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                            pattern="[0-9]{10}"
                                            maxLength="10"
                                            required
                                        />
                                        {errors.hotel.mobileNumber && (
                                            <p className="mt-1 text-sm text-red-600">{errors.hotel.mobileNumber}</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Manager Details Section */}
                            <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-3 sm:p-4 rounded-lg">
                                <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 text-gray-700">Manager Details</h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={managerDetails.name}
                                            onChange={handleManagerInputChange}
                                            className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                            required
                                        />
                                        {errors.manager.name && (
                                            <p className="mt-1 text-sm text-red-600">{errors.manager.name}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={managerDetails.email}
                                            onChange={handleManagerInputChange}
                                            className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                            required
                                        />
                                        {errors.manager.email && (
                                            <p className="mt-1 text-sm text-red-600">{errors.manager.email}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Mobile *</label>
                                        <input
                                            type="text"
                                            name="mobile"
                                            value={managerDetails.mobile}
                                            onChange={handleManagerInputChange}
                                            className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                            required
                                            pattern="[0-9]{10}"
                                            maxLength="10"
                                        />
                                        {errors.manager.mobile && (
                                            <p className="mt-1 text-sm text-red-600">{errors.manager.mobile}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Password {!isEditing && '*'}</label>
                                        <input
                                            type="password"
                                            name="password"
                                            value={managerDetails.password}
                                            onChange={handleManagerInputChange}
                                            className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                            required={!isEditing}
                                        />
                                        {errors.manager.password && (
                                            <p className="mt-1 text-sm text-red-600">{errors.manager.password}</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Pathik Details Section */}
                            <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-3 sm:p-4 rounded-lg">
                                <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 text-gray-700">Pathik Details</h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Pathik Username *</label>
                                        <input
                                            type="text"
                                            name="pathikUsername"
                                            value={currentHotel.pathikUsername}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                            required
                                        />
                                        {errors.hotel.pathikUsername && (
                                            <p className="mt-1 text-sm text-red-600">{errors.hotel.pathikUsername}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Pathik Password {!isEditing && '*'}</label>
                                        <input
                                            type="password"
                                            name="pathikPassword"
                                            value={currentHotel.pathikPassword}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                            required={!isEditing}
                                        />
                                        {errors.hotel.pathikPassword && (
                                            <p className="mt-1 text-sm text-red-600">{errors.hotel.pathikPassword}</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Documents Section */}
                            <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-3 sm:p-4 rounded-lg">
                                <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 text-gray-700">Manager Documents</h3>
                                {documents.map((doc, index) => (
                                    <div key={index} className="mb-4 sm:mb-6 border border-gray-200 rounded-lg p-3 sm:p-4 bg-white">
                                        <div className="flex justify-between items-center mb-2 sm:mb-3">
                                            <h4 className="font-medium text-xs sm:text-sm">Document {index + 1}</h4>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const updated = [...documents];
                                                    updated.splice(index, 1);
                                                    setDocuments(updated);
                                                }}
                                                className="text-red-600 hover:text-red-800 text-xs sm:text-sm"
                                            >
                                                Remove
                                            </button>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Document Type *</label>
                                                <input
                                                    type="text"
                                                    name="documentType"
                                                    value={doc.documentType || ""}
                                                    onChange={(e) => handleDocInputChange(e, index)}
                                                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                                    required
                                                />
                                                {errors.documents[index]?.documentType && (
                                                    <p className="mt-1 text-sm text-red-600">{errors.documents[index].documentType}</p>
                                                )}
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Document Number *</label>
                                                <input
                                                    type="text"
                                                    name="documentNumber"
                                                    value={doc.documentNumber || ""}
                                                    onChange={(e) => handleDocInputChange(e, index)}
                                                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                                    required
                                                />
                                                {errors.documents[index]?.documentNumber && (
                                                    <p className="mt-1 text-sm text-red-600">{errors.documents[index].documentNumber}</p>
                                                )}
                                            </div>
                                        </div>

                                        <div className="mt-3 sm:mt-4">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Upload Document *</label>
                                            <input
                                                type="file"
                                                onChange={(e) => handleFileChange(e, index)}
                                                className="w-full px-3 py-2 text-xs sm:text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                                accept=".jpg, .jpeg, .png"
                                                required={!isEditing}
                                            />
                                            {errors.documents[index]?.file && (
                                                <p className="mt-1 text-sm text-red-600">{errors.documents[index].file}</p>
                                            )}
                                        </div>
                                        {(previewImage[index] || doc.documentUrl) && (
                                            <div className="mt-2">
                                                <img
                                                    src={previewImage[index]
                                                        ? previewImage[index]
                                                        : `${process.env.REACT_APP_BASE_URL}/${doc.documentUrl}`}
                                                    alt={`Document ${index + 1}`}
                                                    className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-md border border-gray-300"
                                                />
                                            </div>
                                        )}
                                    </div>
                                ))}

                                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mt-6">
                                    <button
                                        type="button"
                                        onClick={() => setDocuments([...documents, {}])}
                                        className="mt-2 px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700 focus:outline-none"
                                    >
                                        + Add Document
                                    </button>
                                </div>
                            </div>

                            {errorMsg && (
                                <p className="mt-2 text-sm text-red-600">{errorMsg}</p>
                            )}

                            <div className="mt-6 flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className={`inline-flex justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <svg className="mr-2 h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            {isEditing ? 'Updating...' : 'Adding...'}
                                        </>
                                    ) : isEditing ? 'Update Hotel' : 'Add Hotel'}
                                </button>
                                <button
                                    type="button"
                                    onClick={handleCancel}
                                    className="inline-flex justify-center rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HotelFormModal;


